import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import { decodeJwt, getAccessToken } from '@/lib/api-client';
import {
  activeUnitForUser,
  clearActiveUnitForUser,
  getActiveUnitVersion,
  subscribeActiveUnit,
  writeActiveUnit,
} from '@/lib/crm-active-unit';

import { useMyUnits } from './useBusinessUnits';

/**
 * BU-v2 P3-3 unit switcher state. The caller's "create into" operating unit —
 * what new records without an explicit/parent unit are born into (resolving the
 * multi-unit ambiguity that would otherwise 400). Reach-checked server-side.
 *
 * The selection is kept in localStorage (see crm-active-unit) so the API client
 * can attach it as a header without a store↔api-client import cycle. The user id
 * is taken from the JWT `sub` — the SAME source the API client uses — so the
 * switcher's display and the header can never disagree.
 */
export function useSelectedUnit() {
  const { data, isLoading } = useMyUnits();
  const units = useMemo(() => data?.units ?? [], [data]);
  const reach = data?.reach ?? 'MEMBER';
  const userId = decodeJwt(getAccessToken() ?? '')?.sub ?? null;
  // Shared external store: re-renders EVERY useSelectedUnit consumer whenever
  // any of them changes the selection (the header breadcrumb, the line hook,
  // the list pages) — a per-instance tick only updated the caller that switched.
  const version = useSyncExternalStore(subscribeActiveUnit, getActiveUnitVersion);

  const activeUnits = useMemo(() => units.filter((u) => u.isActive), [units]);

  // Re-read the stored selection whenever the selection changes or identity does.
  const stored = useMemo(() => activeUnitForUser(userId), [userId, version]);

  // Whether the stored pick is a unit the caller can currently reach. The entry
  // gate uses this to decide "straight in" vs "show the chooser".
  const hasValidStoredPick = useMemo(
    () => !!stored && activeUnits.some((u) => u.id === stored),
    [stored, activeUnits],
  );

  // Computed selection: a valid stored pick, else the default unit, else the first.
  const selectedUnitId = useMemo(() => {
    const validStored = hasValidStoredPick ? stored : null;
    return validStored ?? activeUnits.find((u) => u.isDefault)?.id ?? activeUnits[0]?.id ?? null;
  }, [activeUnits, stored, hasValidStoredPick]);

  const selectedUnit = useMemo(
    () => activeUnits.find((u) => u.id === selectedUnitId) ?? null,
    [activeUnits, selectedUnitId],
  );

  // Only AUTO-persist a selection when it's safe to do so without stealing the
  // user's explicit choice: a single-unit user (nothing to choose) or someone
  // who already has a valid stored pick (keep the header in step with the
  // switcher). A multi-unit member with no pick, and an all-units admin, are
  // deliberately left unpersisted so the entry gate can route them to the
  // chooser / control tower instead of silently defaulting.
  const shouldPersistSelection = activeUnits.length <= 1 || hasValidStoredPick;
  useEffect(() => {
    if (userId && shouldPersistSelection && selectedUnitId && stored !== selectedUnitId) {
      writeActiveUnit({ userId, unitId: selectedUnitId });
    }
  }, [userId, shouldPersistSelection, selectedUnitId, stored]);

  const selectUnit = useCallback(
    (unitId: string) => {
      if (userId) writeActiveUnit({ userId, unitId });
    },
    [userId],
  );

  const clearSelection = useCallback(() => {
    clearActiveUnitForUser(userId);
  }, [userId]);

  return {
    units: activeUnits,
    selectedUnitId,
    selectedUnit,
    selectUnit,
    clearSelection,
    reach,
    hasValidStoredPick,
    isLoading,
  };
}
